import { type Database } from "@fitness-app/supabase";

import { type Campaign } from "./Campaign";
import { type IngredientIssueCategory, type IngredientIssueOutcome } from "./DishIssue";
import { type DishRatingActivityData } from "./DishFeedback";
import { type UnreadMessages } from "./Chat";
import { type SurveyType } from "./ClientSurvey";
import { type BodyMeasurements, type ClientPhoto, type TrainingFeedback, type WeightMeasurement } from "./Measurement";
import { type RequiresAction } from "./Order";
import { type Currency, type OrderPaymentType } from "./Product";
import { type ClientArchiveReason, type SubscriptionInterval } from "./ProductClient";

export type NotificationEntity = Database["public"]["Tables"]["notification"]["Row"];

export enum NotificationType {
  ClientTerminated = "client_terminated",
  ClientPurchaseProduct = "client_purchase_product",
  ClientManualPaymentCreated = "client_manual_payment_created",
  ClientManualPaymentRenewal = "client_manual_payment_renewal",
  /** TRE-1840 — a charge failed; until now this only ever left the panel as an e-mail to the owner. */
  ClientPaymentFailed = "client_payment_failed",
  ClientRenewedSubscription = "client_renewed_product",
  ClientSubscriptionPlanUpdated = "client_subscription_plan_updated",
  ClientRenewedAccessPeriod = "client_renewed_access_to_product",
  ClientsImportFinished = "clients_import_finished",
  ClientCancelSubscriptionAtPeriodEnd = "client_cancel_subscription_at_period_end",
  ClientResumeSubscriptionAtPeriodEnd = "client_resume_subscription_at_period_end",
  ProductAccessPeriodEndingIn14Days = "Product_access_period_ending_in_14_days",
  ProductAccessPeriodEndingIn7Days = "Product_access_period_ending_in_7_days",
  ProductAccessPeriodEndingIn3Days = "Product_access_period_ending_in_3_days",
  ProductAccessPeriodEndingIn1Day = "Product_access_period_ending_in_1_day",
  ProductPaymentRequiredAction = "Product_payment_required_action",
  ProductSubscriptionEnd = "Product_subscription_end",
  ProductAccessPeriodEnd = "Product_access_period_end",
  VideoBeenUploaded = "video_been_uploaded",
  ClientSurveyAnswered = "client_survey_answered",
  ClientSurveyAnswersUpdated = "client_survey_answers_updated",
  ClientAddedWeightMeasurement = "client_added_weight_measurement",
  ClientAddedBodyMeasurements = "client_added_body_measurements",
  ClientAddedBodyPhotos = "client_added_body_photos",
  ClientRateWorkout = "client_rate_workout",
  ClientRateDish = "client_rate_dish",
  ClientProgramEndingIn1Day = "client_program_ending_in_1_day",
  ClientProgramEndingIn1Week = "client_program_ending_in_1_week",
  ClientSentVideoRecording = "client_sent_video_recording",
  SupportRequestAssigned = "support_request_assigned",
  SupportCommentMention = "support_comment_mention",
  SupportRequestDueSoon = "support_request_due_soon",
  SupportRequestOverdue = "support_request_overdue",
  /** TRE-1390 — a client submitted a proof/self-report awaiting the trainer's decision. */
  ChallengeCompletionPending = "challenge_completion_pending",
  /** TRE-1655 — board activity for the trainers who manage the challenge. */
  ChallengeParticipantPost = "challenge_participant_post",
  ChallengeFeedComment = "challenge_feed_comment",
  /** TRE-2414 - a client reported a wrong product in the diet tracker or proposed a correction. */
  ClientReportedIngredientIssue = "client_reported_ingredient_issue",
  /** TRE-2414 - a client reported a dish of her meals plan or diary, like trainers do from a meals plan. */
  ClientReportedDishIssue = "client_reported_dish_issue",
}

export enum ClientReminderType {
  NewSurveyAdded = "new_survey_added",
  WeightMeasurementReminder = "weight_measurement_reminder",
  BodyMeasurementReminder = "body_measurement_reminder",
  AccessPeriodEndingReminder = "access_period_ending_reminder",
  NewWorkoutProgramReminder = "new_workout_program_reminder",
  NewPlanProgramReminder = "new_nutrition_plan_reminder",
  ExpectExerciseRecordingReminder = "expect_exercise_recording_reminder",
  PopOutMessage = "pop_out_message",
  /** TRE-1367 — challenge lifecycle nudges inserted by the challenge-reminders scheduler. */
  ChallengeStarted = "challenge_started",
  ChallengeFinished = "challenge_finished",
  ChallengeTaskReminder = "challenge_task_reminder",
  /** TRE-1586 — welcome nudge right after joining (self-join or trainer assignment). */
  ChallengeJoined = "challenge_joined",
  /** TRE-1655 — the trainer published a post on the challenge feed. */
  ChallengeNewPost = "challenge_new_post",
  /** TRE-1390 — the trainer's decision on a client's challenge completion (approval flow). */
  ChallengeCompletionApproved = "challenge_completion_approved",
  ChallengeCompletionRejected = "challenge_completion_rejected",
  /** TRE-1613 — the advisor opened a conversation on its own (admin-initiated). */
  AdvisorConversationStarted = "advisor_conversation_started",
  /** TRE-2298 — the weekly summary is ready; `data.weekStart` is the Monday of that week. */
  WeeklySummary = "weekly_summary",
  /** TRE-2414 - the team decided on the client's product report; `data.outcome` says how. */
  IngredientIssueReviewed = "ingredient_issue_reviewed",
}

export enum NotificationStatus {
  READ = "read",
  UNREAD = "unread",
}

export type ConnectedClient = {
  userId: string | null;
  traineeId?: string | null;
  email: string;
  firstName: string | null;
  lastName: string | null;
  avatarUrl?: string | null;
};

export interface NotificationDocument<T = object> extends Omit<NotificationEntity, "data" | "connectedClient"> {
  connectedClient?: ConnectedClient | null;
  type: NotificationType | ClientReminderType;
  data?: T;
  status: NotificationStatus;
}

export interface GenericNotification<
  IType extends NotificationType | ClientReminderType,
  IData = object,
> extends NotificationDocument<IData> {
  type: IType;
  data: IData;
}

export interface InfoAboutPurchasedProduct {
  productId: string;
  productName: string;
  isFree: boolean;
  paymentType: OrderPaymentType | "installments" | null;
  priceId: string | null;
  installments?: number | null;
  paymentCycle?: number | null;
  price: null | {
    amount: number;
    currency: Currency;
    interval_count?: number;
    interval?: SubscriptionInterval;
  };
}

export interface InfoAboutUploadedVideo {
  productId: string;
  productName: string;
  videoName: string;
  videoId: string;
  lessonId: string;
  lessonName: string;
}

export interface InfoAboutFinishedClientsImport {
  productId: string;
  productName: string;
  totalClients: number;
  importId: string;
  failedClients: number;
  /** Duplicates skipped by policy (TRE-2110); absent on notifications from before. */
  skippedClients?: number;
}

export interface ProductPaymentRequiredActionPayload {
  productId: string;
  productName: string;
  authorName: string;
  authorEmail: string;
  authorId: string;
  action: RequiresAction;
}

export interface ProductSubscriptionEndPayload {
  productId: string;
  productName: string;
  authorName: string;
  authorEmail: string;
  authorId: string;
  endAt: number;
  reason: ClientArchiveReason;
}

export interface CancelOrResumeSubscriptionPayload {
  productId: string;
  productName: string;
  clientName: string;
  clientEmail: string;
  authorId: string;
  periodEnd: number;
}

export interface NewSurveyAddedPayload {
  title: string;
  surveyId: string;
  type: SurveyType;
}

export interface InfoAboutSurveyAnswered {
  title: string;
  surveyId: string;
  type: SurveyType;
}

export interface InfoAboutWorkoutProgram {
  programId: string;
  programName: string;
  endDate: string;
  nextPlan: { name: string; id: string; startDate: string } | null;
}

export interface InfoAboutRequiredExerciseRecording {
  exerciseId: string;
  workoutId: string | null;
  exerciseName: string;
  exerciseParentId: string;
  date: string;
}

export interface ClientExerciseRecordingPayload {
  exerciseId: string;
  exerciseName: string;
  parentExerciseId: string;
  workoutId: string | null;
  workoutDate: string;
  programId: string | null;
}

/**
 * TRE-1840 — a failed charge. `productClientId` is what the panel needs to open
 * the client's payment history, which is where the reaction to a failed payment
 * actually happens.
 */
export interface InfoAboutFailedPayment extends InfoAboutPurchasedProduct {
  orderId: string;
  productClientId: string | null;
  /**
   * Why the charge was refused, in words. For BLIK this is the only place it is
   * ever visible: the reason lives on the transaction at the gateway and no
   * webhook carries it (TRE-2136).
   */
  declineReason?: string | null;
  /** How many times we have tried to collect this installment. */
  attemptCount?: number | null;
}

export type PopOutMessagePayload = Campaign["data"] & {
  campaignId: string;
  deadlineAt?: number | null;
  reason?: "marketing" | "alert";
};

export type NewWeightMeasurementPayload = WeightMeasurement["data"];
export type NewBodyMeasurementsPayload = BodyMeasurements["data"];
export type NewBodyPhotosAddedPayload = ClientPhoto["data"];
export type NewTrainingFeedbackAddedPayload = TrainingFeedback["data"];

// notifications DTO

export interface PurchaseProductNotificationDto {
  action: NotificationType.ClientPurchaseProduct;
  payload: InfoAboutPurchasedProduct;
}

export interface ManualPaymentCreatedNotificationDto {
  action: NotificationType.ClientManualPaymentCreated;
  payload: InfoAboutPurchasedProduct;
}

export interface ManualPaymentRenewalNotificationDto {
  action: NotificationType.ClientManualPaymentRenewal;
  payload: InfoAboutPurchasedProduct;
}

export interface PaymentFailedNotificationDto {
  action: NotificationType.ClientPaymentFailed;
  payload: InfoAboutFailedPayment;
}

export interface RenewedProductNotificationDto {
  action: NotificationType.ClientRenewedSubscription;
  payload: InfoAboutPurchasedProduct;
}

export interface SubscriptionPlanUpdatedProductNotificationDto {
  action: NotificationType.ClientSubscriptionPlanUpdated;
  payload: InfoAboutPurchasedProduct;
}

export interface RenewedAccessProductNotificationDto {
  action: NotificationType.ClientRenewedAccessPeriod;
  payload: InfoAboutPurchasedProduct;
}

export type ClientsImportFinishedNotificationDto = {
  action: NotificationType.ClientsImportFinished;
  payload: InfoAboutFinishedClientsImport;
};

export type ProductPaymentRequiredActionNotificationDto = {
  action: NotificationType.ProductPaymentRequiredAction;
  payload: ProductPaymentRequiredActionPayload;
};

export type ProductSubscriptionEndNotificationDto = {
  action: NotificationType.ProductSubscriptionEnd;
  payload: ProductSubscriptionEndPayload;
};

export type ProductAccessPeriodEndNotificationDto = {
  action: NotificationType.ProductAccessPeriodEnd;
  payload: ProductSubscriptionEndPayload;
};

export interface VideoBeenUploadedNotificationDto {
  action: NotificationType.VideoBeenUploaded;
  payload: InfoAboutUploadedVideo;
}

export type ProductAccessPeriodEndingNotificationDto = {
  action:
    | NotificationType.ProductAccessPeriodEndingIn14Days
    | NotificationType.ProductAccessPeriodEndingIn7Days
    | NotificationType.ProductAccessPeriodEndingIn3Days
    | NotificationType.ProductAccessPeriodEndingIn1Day;
  payload: ProductSubscriptionEndPayload;
};

export type ClientResumeSubscriptionAtPeriodEndNotificationDto = {
  action: NotificationType.ClientResumeSubscriptionAtPeriodEnd;
  payload: CancelOrResumeSubscriptionPayload;
};

export type ClientCancelSubscriptionAtPeriodEndNotificationDto = {
  action: NotificationType.ClientCancelSubscriptionAtPeriodEnd;
  payload: CancelOrResumeSubscriptionPayload;
};

export type ClientAnsweredSurveyNotificationDto = {
  action: NotificationType.ClientSurveyAnswered;
  payload: InfoAboutSurveyAnswered;
};

export type ClientProgramEndingIn1WeekNotificationDto = {
  action: NotificationType.ClientProgramEndingIn1Week;
  payload: InfoAboutWorkoutProgram;
};

export type ClientProgramEndingIn1DayNotificationDto = {
  action: NotificationType.ClientProgramEndingIn1Day;
  payload: InfoAboutWorkoutProgram;
};

export type ClientSurveyAnswersUpdatedNotificationDto = {
  action: NotificationType.ClientSurveyAnswersUpdated;
  payload: InfoAboutSurveyAnswered;
};

export type NewSurveyAddedNotificationDto = {
  action: ClientReminderType.NewSurveyAdded;
  payload: NewSurveyAddedPayload;
};

export type ClientSentVideoRecordingNotificationDto = {
  action: NotificationType.ClientSentVideoRecording;
  payload: ClientExerciseRecordingPayload;
};

// notifications
export type SubscriptionPlanUpdatedProductNotification = GenericNotification<
  NotificationType.ClientSubscriptionPlanUpdated,
  InfoAboutPurchasedProduct
>;
export type VideoBeenUploadedNotification = GenericNotification<
  NotificationType.VideoBeenUploaded,
  InfoAboutUploadedVideo
>;

export type PurchaseProductNotification = GenericNotification<
  NotificationType.ClientPurchaseProduct,
  InfoAboutPurchasedProduct
>;

export type ManualPaymentCreatedNotification = GenericNotification<
  NotificationType.ClientManualPaymentCreated,
  InfoAboutPurchasedProduct
>;

export type ManualPaymentRenewalNotification = GenericNotification<
  NotificationType.ClientManualPaymentRenewal,
  InfoAboutPurchasedProduct
>;

export type PaymentFailedNotification = GenericNotification<
  NotificationType.ClientPaymentFailed,
  InfoAboutFailedPayment
>;

export type RenewedProductNotification = GenericNotification<
  NotificationType.ClientRenewedSubscription,
  InfoAboutPurchasedProduct
>;

export type RenewedAccessProductNotification = GenericNotification<
  NotificationType.ClientRenewedAccessPeriod,
  InfoAboutPurchasedProduct
>;

export type ClientsImportFinishedNotification = GenericNotification<
  NotificationType.ClientsImportFinished,
  InfoAboutFinishedClientsImport
>;

export type ProductPaymentRequiredActionNotification = GenericNotification<
  NotificationType.ProductPaymentRequiredAction,
  ProductPaymentRequiredActionPayload
>;

export type ProductSubscriptionEnd = GenericNotification<
  NotificationType.ProductSubscriptionEnd,
  ProductSubscriptionEndPayload
>;

export type ProductAccessPeriodEndNotification = GenericNotification<
  NotificationType.ProductAccessPeriodEnd,
  ProductSubscriptionEndPayload
>;

export type ProductAccessPeriodEndingNotification = GenericNotification<
  | NotificationType.ProductAccessPeriodEndingIn14Days
  | NotificationType.ProductAccessPeriodEndingIn7Days
  | NotificationType.ProductAccessPeriodEndingIn3Days
  | NotificationType.ProductAccessPeriodEndingIn1Day,
  ProductSubscriptionEndPayload
>;

export type ClientCancelSubscriptionAtPeriodEndNotification = GenericNotification<
  NotificationType.ClientCancelSubscriptionAtPeriodEnd,
  CancelOrResumeSubscriptionPayload
>;

export type ClientResumeSubscriptionAtPeriodEndNotification = GenericNotification<
  NotificationType.ClientResumeSubscriptionAtPeriodEnd,
  CancelOrResumeSubscriptionPayload
>;

export type ClientAnsweredSurveyNotification = GenericNotification<
  NotificationType.ClientSurveyAnswered,
  InfoAboutSurveyAnswered
>;

export type ClientSurveyAnswersUpdatedNotification = GenericNotification<
  NotificationType.ClientSurveyAnswersUpdated,
  InfoAboutSurveyAnswered
>;

export type ClientAddedWeightMeasurementNotification = GenericNotification<
  NotificationType.ClientAddedWeightMeasurement,
  NewWeightMeasurementPayload
>;

export type ClientAddedBodyMeasurementsNotification = GenericNotification<
  NotificationType.ClientAddedBodyMeasurements,
  NewBodyMeasurementsPayload
>;

export type ClientAddedBodyPhotosNotification = GenericNotification<
  NotificationType.ClientAddedBodyPhotos,
  NewBodyPhotosAddedPayload
>;

export type ClientAddedWorkoutRateNotification = GenericNotification<
  NotificationType.ClientRateWorkout,
  NewTrainingFeedbackAddedPayload
>;

export type ClientAddedDishRateNotification = GenericNotification<
  NotificationType.ClientRateDish,
  DishRatingActivityData
>;

/** TRE-2414 - written by `report_ingredient_issue`; `connectedClient` is the reporting client. */
export interface ClientReportedIngredientIssuePayload {
  issueId: string;
  ingredientId: string;
  ingredientName: string;
  categories: IngredientIssueCategory[];
}

export type ClientReportedIngredientIssueNotification = GenericNotification<
  NotificationType.ClientReportedIngredientIssue,
  ClientReportedIngredientIssuePayload
>;

/** TRE-2414 - written by `report_dish_issue`; `connectedClient` is the reporting client. */
export interface ClientReportedDishIssuePayload {
  issueId: string;
  dishId: string;
  dishName: string;
  categories: IngredientIssueCategory[];
}

export type ClientReportedDishIssueNotification = GenericNotification<
  NotificationType.ClientReportedDishIssue,
  ClientReportedDishIssuePayload
>;

export type ClientProgramEndingIn1WeekNotification = GenericNotification<
  NotificationType.ClientProgramEndingIn1Week,
  InfoAboutWorkoutProgram
>;

export type ClientProgramEndingIn1DayNotification = GenericNotification<
  NotificationType.ClientProgramEndingIn1Day,
  InfoAboutWorkoutProgram
>;

export type ClientSentVideoRecordingNotification = GenericNotification<
  NotificationType.ClientSentVideoRecording,
  ClientExerciseRecordingPayload
>;

// Support board (TRE-1550): ownerId = the team user being notified; resourceId = requestId.
export interface SupportRequestNotificationPayload {
  requestId: string;
  requestTitle: string;
  // Who assigned / mentioned — absent on cron-driven deadline reminders.
  actorName?: string | null;
  commentId?: string;
  dueDate?: string;
}

export type SupportRequestAssignedNotification = GenericNotification<
  NotificationType.SupportRequestAssigned,
  SupportRequestNotificationPayload
>;

export type SupportCommentMentionNotification = GenericNotification<
  NotificationType.SupportCommentMention,
  SupportRequestNotificationPayload
>;

export type SupportRequestDueSoonNotification = GenericNotification<
  NotificationType.SupportRequestDueSoon,
  SupportRequestNotificationPayload
>;

export type SupportRequestOverdueNotification = GenericNotification<
  NotificationType.SupportRequestOverdue,
  SupportRequestNotificationPayload
>;

export type NewSurveyAddedReminder = GenericNotification<ClientReminderType.NewSurveyAdded, NewSurveyAddedPayload>;

export interface ChallengeNotificationPayload {
  challengeId: string;
  challengeName: string;
  /** Present on start nudges. */
  endDate?: string;
  /** Present on task reminders for weekday-scheduled tasks ("today's the day"). */
  taskTitle?: string;
}

export type ChallengeStartedReminder = GenericNotification<
  ClientReminderType.ChallengeStarted,
  ChallengeNotificationPayload
>;
export type ChallengeFinishedReminder = GenericNotification<
  ClientReminderType.ChallengeFinished,
  ChallengeNotificationPayload
>;
export type ChallengeTaskReminder = GenericNotification<
  ClientReminderType.ChallengeTaskReminder,
  ChallengeNotificationPayload
>;
export type ChallengeJoinedReminder = GenericNotification<
  ClientReminderType.ChallengeJoined,
  ChallengeNotificationPayload
>;

/**
 * TRE-1655 — a trainer post on the challenge feed. `feedMode` rides along so the mobile center can
 * deep-link straight into the feed (the screen needs to know what the client may do there).
 */
export interface ChallengeNewPostPayload extends ChallengeNotificationPayload {
  postId: string;
  feedMode: string;
  /** First line of the post, for the notification body. */
  excerpt?: string;
}

export type ChallengeNewPostReminder = GenericNotification<
  ClientReminderType.ChallengeNewPost,
  ChallengeNewPostPayload
>;

// TRE-1390 — approval flow. Client-facing decisions reuse the challenge payload (taskTitle present);
// the trainer-facing "pending" notification additionally carries the completionId (idempotency key)
// and renders with connectedClient = the submitting client.
export interface ChallengeCompletionPendingPayload extends ChallengeNotificationPayload {
  completionId: string;
}

export type ChallengeCompletionApprovedReminder = GenericNotification<
  ClientReminderType.ChallengeCompletionApproved,
  ChallengeNotificationPayload
>;
export type ChallengeCompletionRejectedReminder = GenericNotification<
  ClientReminderType.ChallengeCompletionRejected,
  ChallengeNotificationPayload
>;
export type ChallengeCompletionPendingNotification = GenericNotification<
  NotificationType.ChallengeCompletionPending,
  ChallengeCompletionPendingPayload
>;

// TRE-1655 — board activity aimed at the challenge's MANAGERS (challenge_manager), so a trainer
// learns about a participant's post or a reply without watching the feed. Which of these a manager
// receives is their own per-challenge switch.
export interface ChallengeParticipantPostPayload extends ChallengeNotificationPayload {
  postId: string;
  authorName: string;
  excerpt?: string;
}

export interface ChallengeFeedCommentPayload extends ChallengeNotificationPayload {
  postId: string;
  commentId: string;
  authorName: string;
  /** Whether the commented post was written by a trainer — decides which switch applies. */
  onTrainerPost: boolean;
  excerpt?: string;
}

export type ChallengeParticipantPostNotification = GenericNotification<
  NotificationType.ChallengeParticipantPost,
  ChallengeParticipantPostPayload
>;
export type ChallengeFeedCommentNotification = GenericNotification<
  NotificationType.ChallengeFeedComment,
  ChallengeFeedCommentPayload
>;
export type AddWeightMeasurementReminder = GenericNotification<ClientReminderType.WeightMeasurementReminder, null>;
export type AddBodyMeasurementsReminder = GenericNotification<ClientReminderType.BodyMeasurementReminder, null>;
export type ExpectExerciseRecordingReminder = GenericNotification<
  ClientReminderType.ExpectExerciseRecordingReminder,
  InfoAboutRequiredExerciseRecording
>;
export type PopOutClientNotification = GenericNotification<ClientReminderType.PopOutMessage, PopOutMessagePayload>;

export type PurchaseProductNotificationId = PurchaseProductNotification & { id: string };

export type WithId<T extends object> = T & { id: string };

export type OwnerNotifications =
  | WithId<PurchaseProductNotification>
  | WithId<RenewedProductNotification>
  | WithId<RenewedAccessProductNotification>
  | WithId<ClientsImportFinishedNotification>
  | WithId<ClientCancelSubscriptionAtPeriodEndNotification>
  | WithId<ClientResumeSubscriptionAtPeriodEndNotification>
  | WithId<SubscriptionPlanUpdatedProductNotification>
  | WithId<VideoBeenUploadedNotification>
  | WithId<ManualPaymentCreatedNotification>
  | WithId<ManualPaymentRenewalNotification>
  | WithId<PaymentFailedNotification>;

export type OwnerNotificationsDto =
  | PurchaseProductNotificationDto
  | RenewedProductNotificationDto
  | RenewedAccessProductNotificationDto
  | ClientsImportFinishedNotificationDto
  | ClientCancelSubscriptionAtPeriodEndNotificationDto
  | ClientResumeSubscriptionAtPeriodEndNotificationDto
  | SubscriptionPlanUpdatedProductNotificationDto
  | VideoBeenUploadedNotificationDto
  | ManualPaymentCreatedNotificationDto
  | ManualPaymentRenewalNotificationDto
  | PaymentFailedNotificationDto;

export type ClientNotificationsDto =
  | ProductPaymentRequiredActionNotificationDto
  | ProductSubscriptionEndNotificationDto
  | ProductAccessPeriodEndNotificationDto
  | ProductAccessPeriodEndingNotificationDto;

export type ClientNotifications =
  | WithId<ProductPaymentRequiredActionNotification>
  | WithId<ProductSubscriptionEnd>
  | WithId<ProductAccessPeriodEndNotification>
  | WithId<ProductAccessPeriodEndingNotification>;

export type TrainerNotifications = WithId<
  | ClientAnsweredSurveyNotification
  | ClientSurveyAnswersUpdatedNotification
  | ClientAddedBodyPhotosNotification
  | ClientAddedBodyMeasurementsNotification
  | ClientAddedWeightMeasurementNotification
  | ClientAddedWorkoutRateNotification
  | ClientAddedDishRateNotification
  | ClientProgramEndingIn1WeekNotification
  | ClientProgramEndingIn1DayNotification
  | ClientSentVideoRecordingNotification
  | SupportRequestAssignedNotification
  | SupportCommentMentionNotification
  | SupportRequestDueSoonNotification
  | SupportRequestOverdueNotification
  | ChallengeCompletionPendingNotification
  | ChallengeParticipantPostNotification
  | ChallengeFeedCommentNotification
  | ClientReportedIngredientIssueNotification
  | ClientReportedDishIssueNotification
>;

/** TRE-1613 — the advisor opened a conversation on its own (admin-initiated). */
export interface AdvisorConversationStartedPayload {
  sessionId: string;
}

export type AdvisorConversationStartedReminder = GenericNotification<
  ClientReminderType.AdvisorConversationStarted,
  AdvisorConversationStartedPayload
>;

/** TRE-2298 — the weekly summary of the ISO week starting on `weekStart` (YYYY-MM-DD) is ready. */
export interface WeeklySummaryPayload {
  weekStart: string;
}

export type WeeklySummaryReminder = GenericNotification<ClientReminderType.WeeklySummary, WeeklySummaryPayload>;

/** TRE-2409 — the client got a training plan; the app opens it, or its preview once it is no longer active. */
export interface NewWorkoutProgramPayload {
  programId: string;
  programName: string;
}

export type NewWorkoutProgramReminder = GenericNotification<
  ClientReminderType.NewWorkoutProgramReminder,
  NewWorkoutProgramPayload
>;

/** TRE-2409 — the client got a meals plan; `planId` is the client's own copy in `client_meals_plan`. */
export interface NewNutritionPlanPayload {
  planId: string;
  planName: string;
}

export type NewNutritionPlanReminder = GenericNotification<
  ClientReminderType.NewPlanProgramReminder,
  NewNutritionPlanPayload
>;

/** TRE-2414 - written by `resolve_trainee_issue` for the client who reported the product or the dish. */
export interface IngredientIssueReviewedPayload {
  issueId: string;
  ingredientId: string | null;
  dishId: string | null;
  /** The product's or the dish's name. */
  name: string;
  outcome: IngredientIssueOutcome;
  categories: IngredientIssueCategory[] | null;
}

export type IngredientIssueReviewedReminder = GenericNotification<
  ClientReminderType.IngredientIssueReviewed,
  IngredientIssueReviewedPayload
>;

export type ClientReminders =
  | WithId<NewSurveyAddedReminder>
  | WithId<AddWeightMeasurementReminder>
  | WithId<AddBodyMeasurementsReminder>
  | WithId<ExpectExerciseRecordingReminder>
  | WithId<PopOutClientNotification>
  | WithId<ChallengeStartedReminder>
  | WithId<ChallengeFinishedReminder>
  | WithId<ChallengeTaskReminder>
  | WithId<ChallengeJoinedReminder>
  | WithId<ChallengeNewPostReminder>
  | WithId<ChallengeCompletionApprovedReminder>
  | WithId<ChallengeCompletionRejectedReminder>
  | WithId<AdvisorConversationStartedReminder>
  | WithId<WeeklySummaryReminder>
  | WithId<NewWorkoutProgramReminder>
  | WithId<NewNutritionPlanReminder>
  | WithId<IngredientIssueReviewedReminder>;

export type AppNotification = ClientNotifications | OwnerNotifications | ClientReminders | TrainerNotifications;

export interface InitialNotificationsStateEvent {
  type: "initial";
  unreadMessages: UnreadMessages | null;
  unreadNotifications: number;
}

export interface NewNotificationEvent {
  type: "newUnreadNotification";
  notificationType: AppNotification["type"];
  data: AppNotification;
}

export interface UpdatedNotificationEvent {
  type: "updatedNotification";
  notificationType: AppNotification["type"];
  data: AppNotification;
}

export type InComingHttpNotificationEvents = NewNotificationEvent | UpdatedNotificationEvent;

export type OutgoingNotificationsRealtimeEvents =
  | InitialNotificationsStateEvent
  | (NewNotificationEvent & { unreadNotifications: number })
  | (UpdatedNotificationEvent & { unreadNotifications: number });
